import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import LinkOffIcon from "@mui/icons-material/LinkOff";
import YouTubeIcon from "@mui/icons-material/YouTube";
import { Avatar, Chip, Stack, TableCell, TableRow, Typography } from "@mui/material";
import dayjs from "dayjs";
import { useContext } from "react";
import { useIntl } from "../../../contexts/IntlContext";
import { AuthContext } from "../../../contexts/AuthContext";
import { isAuthAdmin } from "../../../utils/authRole";
import { YOUTUBE_STATUS_PILL_LEVEL_BY_TOKEN_STATUS } from "../../../constants/youtubeTokenStatus";
import { useYoutubeOAuthPopup } from "../../../hooks/useYoutubeOAuthPopup";
import IconButton from "../../../components/ui/IconButton";
import LoadingButton from "../../../components/ui/LoadingButton";
import StatusPill from "../../../components/ui/StatusPill";
import StatusToggle from "../../../components/StatusToggle";
import {
  YoutubeAdminAccount,
  listYoutubeAdminAccounts,
  startYoutubeAdminAccountConnection,
} from "../../../services/youtubeAdminAccounts";

const STATUS_LABEL_ID_BY_TOKEN_STATUS: Record<string, string> = {
  CONNECTED: "youtubeAdminAccounts.status.connected",
  RENEWAL_ERROR: "youtubeAdminAccounts.status.renewalError",
  REVOKED: "youtubeAdminAccounts.status.requiresReconnect",
  MISSING_SCOPE: "youtubeAdminAccounts.status.requiresReconnect",
  DISCONNECTED: "youtubeAdminAccounts.status.disconnected",
  NO_ACCOUNT: "youtubeAdminAccounts.status.disconnected",
};

type Props = {
  account: YoutubeAdminAccount;
  onChanged: () => Promise<void>;
  onToggleActive: () => Promise<void>;
  onEdit: () => void;
  onUnlink: () => void;
  onDelete: () => void;
};

export default function YoutubeAdminAccountRow({
  account,
  onChanged,
  onToggleActive,
  onEdit,
  onUnlink,
  onDelete,
}: Props) {
  const intl = useIntl();
  const { authInfo } = useContext(AuthContext);
  const { connect, loading: connecting } = useYoutubeOAuthPopup({
    start: popup => startYoutubeAdminAccountConnection(account.id, { popup }),
    getConnected: async () => {
      const accounts = await listYoutubeAdminAccounts();
      return accounts.find(a => a.id === account.id)?.tokenStatus === "CONNECTED";
    },
    onConnected: onChanged,
  });

  const statusLevel = YOUTUBE_STATUS_PILL_LEVEL_BY_TOKEN_STATUS[account.tokenStatus] ?? "neutral";
  const statusLabelId =
    STATUS_LABEL_ID_BY_TOKEN_STATUS[account.tokenStatus] ?? "youtubeAdminAccounts.status.disconnected";
  const lastSyncedLabel = account.lastSyncedAt
    ? dayjs(account.lastSyncedAt).format("DD/MM/YYYY [às] HH:mm")
    : "—";
  const isConnected = account.tokenStatus === "CONNECTED";
  const isPrincipalAccount = account.id === 1;
  const canDelete = isAuthAdmin(authInfo?.role);
  // Admin/suporte nunca inicia OAuth em nome do proprietário (self-service) e não faz
  // sentido oferecer "reconectar" quando já está conectado.
  const canConnect = !account.ownerManaged && !isConnected;

  return (
    <TableRow hover>
      <TableCell>
        <Stack direction="row" spacing={1} alignItems="center">
          {account.channelImageUrl ? (
            <Avatar src={account.channelImageUrl} sx={{ width: 24, height: 24 }} />
          ) : null}
          <Stack spacing={0}>
            <Stack direction="row" spacing={0.5} alignItems="center">
              <Typography variant="body2">{account.channelName || account.name}</Typography>
              {account.ownerManaged ? (
                <Chip
                  size="small"
                  label={intl.formatMessage({ id: "youtubeAdminAccounts.ownerManagedChip" })}
                />
              ) : null}
            </Stack>
            {account.connectedEmail ? (
              <Typography variant="caption" sx={{ color: "text.secondary" }}>
                {account.connectedEmail}
              </Typography>
            ) : null}
          </Stack>
        </Stack>
      </TableCell>
      <TableCell>
        <StatusPill size="small" level={statusLevel} label={intl.formatMessage({ id: statusLabelId })} />
      </TableCell>
      <TableCell align="right">{account.scheduleLimit}</TableCell>
      <TableCell>
        <StatusToggle
          checked={account.active}
          size="small"
          tooltip={intl.formatMessage({ id: "youtubeAdminAccounts.form.active" })}
          confirmTitle={
            account.active
              ? intl.formatMessage({ id: "youtubeAdminAccounts.deactivateConfirmTitle" })
              : undefined
          }
          confirmMessage={
            account.active
              ? intl.formatMessage(
                  { id: "youtubeAdminAccounts.deactivateConfirmMessage" },
                  { name: account.channelName || account.name, count: account.activeScheduleCount }
                )
              : undefined
          }
          onToggle={() => onToggleActive()}
        />
      </TableCell>
      <TableCell>{lastSyncedLabel}</TableCell>
      <TableCell align="right">
        <Stack direction="row" spacing={0.5} justifyContent="flex-end">
          {canConnect ? (
            <LoadingButton
              variant="outlined"
              color="warning"
              size="small"
              loading={connecting}
              onClick={() => connect()}
              startIcon={<YouTubeIcon />}
              fullWidth={false}
            >
              {intl.formatMessage({ id: "youtubeAdminAccounts.connect" })}
            </LoadingButton>
          ) : null}
          <IconButton tooltip={intl.formatMessage({ id: "common.edit" })} onClick={onEdit}>
            <EditIcon fontSize="small" />
          </IconButton>
          <span>
            <IconButton
              tooltip={intl.formatMessage({ id: "youtubeAdminAccounts.unlink" })}
              onClick={onUnlink}
              disabled={!isConnected}
            >
              <LinkOffIcon fontSize="small" />
            </IconButton>
          </span>
          {canDelete ? (
            <span>
              <IconButton
                tooltip={intl.formatMessage({ id: "common.delete" })}
                onClick={onDelete}
                disabled={isPrincipalAccount || account.ownerManaged || account.activeScheduleCount > 0}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </span>
          ) : null}
        </Stack>
      </TableCell>
    </TableRow>
  );
}
